import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Logout as LogoutIcon,
  Star as StarIcon,
  FolderOpen as FolderOpenIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { formatProjectDate, getProjectTimestamp, isProjectReadyToDownload } from '../utils/projectState';

const UserProfile = () => {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchAccountData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/projects');
      setProjects(response.data.projects || []);
      setError('');
    } catch (err) {
      console.error('Failed to fetch account data:', err);
      setError('Failed to load account information.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Refresh user data once on mount (credits, etc.) then load projects.
    // `refreshUser` is intentionally excluded from the dependency array: even
    // though it is now stable (useCallback in AuthContext), keeping it out
    // makes the intent explicit – this effect must only fire once on mount,
    // not every time the user object updates.
    refreshUser?.();
    fetchAccountData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshUser excluded intentionally (see comment above)
  }, [fetchAccountData]);

  const handleLogout = async () => {
    setActionLoading(true);
    await logout();
    navigate('/login');
  };

  const snapshot = useMemo(() => {
    const sortedProjects = [...projects].sort(
      (a, b) => new Date(getProjectTimestamp(b) || 0) - new Date(getProjectTimestamp(a) || 0)
    );

    return {
      totalProjects: sortedProjects.length,
      readyToDownload: sortedProjects.filter(isProjectReadyToDownload).length,
      lastProjectDate: getProjectTimestamp(sortedProjects[0]),
      studioCredits: user?.themeCredits || 0,
    };
  }, [projects, user?.themeCredits]);

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'WordExPress User';

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 8, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Account Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage your account details and keep an eye on credits and finished projects.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom>
          Account Information
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <PersonIcon color="primary" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {fullName}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <EmailIcon color="primary" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {user?.email || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Member Since
            </Typography>
            <Typography variant="body2">
              {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Account Status
            </Typography>
            <Typography variant="body2">
              Active
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <StarIcon color="primary" />
                <Typography variant="h6">Studio Credits</Typography>
              </Box>
              <Typography variant="h3" fontWeight="bold" sx={{ mb: 1 }}>
                {snapshot.studioCredits}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {snapshot.studioCredits > 0
                  ? 'Available for custom theme generation.'
                  : 'Buy credits when you need more custom theme runs.'}
              </Typography>
              <Button component={RouterLink} to="/pricing" variant="outlined" fullWidth>
                View Pricing
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <CheckCircleIcon color="success" />
                <Typography variant="h6">Workspace Snapshot</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {snapshot.totalProjects} total project{snapshot.totalProjects === 1 ? '' : 's'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {snapshot.readyToDownload} ready to download
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Last activity: {snapshot.lastProjectDate ? formatProjectDate(snapshot.lastProjectDate) : 'No projects yet'}
              </Typography>
              <Button
                component={RouterLink}
                to="/projects"
                variant="outlined"
                fullWidth
                startIcon={<FolderOpenIcon />}
              >
                Open Projects
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Button
        variant="outlined"
        color="error"
        startIcon={<LogoutIcon />}
        onClick={() => setLogoutDialogOpen(true)}
        fullWidth
      >
        Logout
      </Button>

      <Dialog open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)}>
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to logout?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleLogout} color="error" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Logout'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserProfile;
